import { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import {
  TableContainer,
  TableHeader,
  TableContent,
  FilterChips,
  FilterChipsOptionsProps,
  TableHeaderProps,
  STATUS_INDICATOR_VARIANTS,
} from "@cdkglobal/radial";
import { RowData, RowDataValueType } from "../../../../../types";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { formatFieldValueToString, matchesSearch } from "../../../helpers";
import { StatusIndicatorWithTooltip } from "../../../../common/Tooltip";
import { FILE_UPLOAD_STATE_CONFIG, STATUS_VARIANT_BY_LABEL } from "./constants";
import { BULK_IMPORT_FIELD_KEYS } from "../../../constants";

interface FileUploadAgGridTableProps {
  languageSpecificColDefs: ColDef[];
  dataToDisplay: RowData[];
  fileUploadState: "validationComplete" | "importComplete";
}

export default function FileUploadTable({
  languageSpecificColDefs,
  dataToDisplay,
  fileUploadState,
}: Readonly<FileUploadAgGridTableProps>): JSX.Element {
  const gridRef = useRef<AgGridReact>(null);
  const stateConfig = FILE_UPLOAD_STATE_CONFIG[fileUploadState];
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<FilterChipsOptionsProps[]>(
    stateConfig.filterOptions,
  );

  const autoSizeColumns = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.autoSizeAllColumns(false);
  }, []);

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchValue(event.target.value);
  };

  const handleSearchInputClear = () => setSearchValue("");

  const getSwitchIndicatorVariants = (status: string) =>
    STATUS_VARIANT_BY_LABEL[
      status.toLowerCase() as keyof typeof STATUS_VARIANT_BY_LABEL
    ] ?? STATUS_INDICATOR_VARIANTS.INFORMATION;

  const renderField = useCallback(
    (fieldName: string, fieldValue: RowDataValueType) => {
      if (fieldValue == null) return "-";

      const isStatusField =
        (fieldName === BULK_IMPORT_FIELD_KEYS.RECORD_IMPORT_STATUS ||
          fieldName === BULK_IMPORT_FIELD_KEYS.RECORD_VALIDATION_STATUS) &&
        typeof fieldValue === "string";

      if (isStatusField) {
        return (
          <StatusIndicatorWithTooltip
            label={fieldValue}
            variant={getSwitchIndicatorVariants(fieldValue)}
            tooltip={fieldValue}
          />
        );
      }

      return formatFieldValueToString(fieldName, fieldValue);
    },
    [],
  );

  const filteredDataToDisplay = useMemo(() => {
    const selectedValues = new Set(
      statusFilter.map((f) => (f.value as string).toLowerCase()),
    );

    return dataToDisplay.filter((row) => {
      const matchesSearchTerm = Object.values(row).some((value) =>
        matchesSearch(value, searchValue),
      );

      const statusField = row[stateConfig.statusField];
      const matchesStatus =
        typeof statusField === "string" &&
        selectedValues.has(statusField.toLowerCase());

      return matchesSearchTerm && matchesStatus;
    });
  }, [searchValue, statusFilter, dataToDisplay, stateConfig.statusField]);

  const tableHeaderProps: TableHeaderProps = {
    id: "file-upload-header",
    dataTestId: "file-upload-header",
    headerTitle: `${stateConfig.headerTitle} (${filteredDataToDisplay.length})`,
    hideBorder: false,
    hideTableTitle: false,
    isAlwaysExpanded: true,

    // Downloadable branch
    isDownloadable: false,

    // Printable branch — false, no print props allowed
    isPrintable: false,

    // Searchable branch
    isSearchable: true,
    onSearchInputChange: handleSearchInputChange,
    onSearchInputClear: handleSearchInputClear,
    searchInputName: "table-search-input",
    searchInputValue: searchValue,
    searchPlaceholder: "Search...",
  };

  const tableHeaderColumns: ColDef[] = useMemo(() => {
    const baseColumns = languageSpecificColDefs.map((col) => ({
      ...col,
      cellRenderer: (params: ICellRendererParams) =>
        renderField(col.field as string, params.value),
    }));

    const pinnedColumns: ColDef[] = stateConfig.pinnedColumns.map((col) => ({
      ...col,
      cellRenderer: (params: ICellRendererParams) =>
        renderField(col.field, params.value),
      pinned: "left",
    }));

    return [...pinnedColumns, ...baseColumns];
  }, [languageSpecificColDefs, renderField, stateConfig.pinnedColumns]);

  return (
    <TableContainer agGridVersion="31.3.4">
      <TableHeader {...tableHeaderProps} />

      <FilterChips
        options={stateConfig.filterOptions}
        value={statusFilter}
        onChange={(selected: FilterChipsOptionsProps[]) =>
          setStatusFilter(selected)
        }
        style={{ margin: "0.5rem" }}
        dataTestId="filter-option-chips"
      />

      <TableContent data-testid="table-content">
        <AgGridReact
          className="ag-theme-balham"
          containerStyle={{ height: "40vh", width: "100%" }}
          defaultColDef={{
            filter: true,
            sortable: true,
            resizable: true,
            minWidth: 160,
            autoHeight: true,
            wrapText: true,
          }}
          columnDefs={tableHeaderColumns}
          onFirstDataRendered={autoSizeColumns}
          rowData={filteredDataToDisplay}
          suppressPaginationPanel
          ref={gridRef}
        />
      </TableContent>
    </TableContainer>
  );
}
